import { analytics } from './analytics.js';
import { buildTrustDeck, resolveTrustLabels, resolveTrustRhythm } from './resolve-trust.js';
import { withSafeArea } from './chrome.js';
import { createTrustBoard } from './trust-cards.js';
import { hiddenTrustGroups, hideTrustGroup, markTrustSeen, resetTrustSeen, showTrustGroups, trustAlreadySeen } from './visit-marks.js';
import { origin } from './snippet.js';
import { buildPageKey, onPathChange } from './page.js';
import { runtime } from './runtime.js';
import { guidPattern, optOutDoneMs, optOutOfferMs, trustCardGroups, trustOptOutGroups } from './constants.js';
import { urlRulesAllow } from './url-rules.js';

/**
 * Renders launcher + panel and wires the open/close behavior.
 * @param {object|null} config - Parsed config JSON or null on failure
 */

/**
 * The corner's rotating proof: the trust cards, their opt-out, the way back from it, and the
 * rotation between them. Lifted out of init() as the first of its surfaces — everything private to
 * the corner is a closure variable in here, and the handful of things the rest of the widget shares
 * with it travels through ctx.
 *
 * Reads from the rest of the instance, all of it destructured below so the bodies below read exactly
 * as they did inside init(): the launcher and panel elements, the shared state, and four functions
 * that belong to surfaces still living in init() — opening the panel, asking it for a screen,
 * repainting after a theme change, and the live-settings broadcast a dismissal has to reach.
 * @param {object} ctx - The instance context (see init)
 * @returns {object} The corner's handles for the rest of the instance
 */
export function createTrustSurface(ctx) {
    const { options, button, state, setOpen, requestScreen, repaintForTheme, applyLiveTrust } = ctx;

    let trustCardTimer = null;

    /* What the corner was last built from, so the way back can rebuild it: a restore has to deal the
     * groups the visitor took back, and only a payload holds them. Starts as the config this page load
     * read and follows every save broadcast (applyLiveTrust). */
    state.trustPayload = ctx.config;

    /* The opt-out offer standing in the dismissed card's place, and the timers that take it away — its
     * own, not the bubbles': the two corners are dismissed independently and a shared list would let
     * one cancel the other's confirmation. */
    let trustOptOutTimers = [];

    /**
     * A tapped card opens the widget on that review, that project or — for every seal-shaped card and
     * the rating, none of which has a detail of its own — the provider's profile screen, where all of
     * them are documented. The card is a button rather than a link for exactly this: all of it lives in
     * the widget, and sending a visitor to a profile page would take them off the website they came to
     * read.
     * @param {{kind: string, review: object, project: object, seal: object, ratingSeal: object}} item -
     *     The shown card
     */
    function openTrustCard(item) {
        // Every seal-shaped card and the rating card go the same way: none of them has a detail page of
        // its own, and the profile is where all of them are documented — the earned seals, the awards
        // the provider listed, the "Empfehlungssiegel" and the reviews the average rests on.
        if (item.kind !== 'review' && item.kind !== 'project' && item.kind !== 'socialpost') {
            // The profile screen is the seal's citation, and the API route already knows how to reach
            // it whether the frame is mounted, starting up or not there yet. The rating card lands on
            // the profile's review list ('reviews'): the reviews are the citation of the average it
            // claims, and a visitor who tapped the number should not have to find them themselves.
            requestScreen(item.kind === 'ratingseal' ? 'reviews' : 'profile');
            setOpen(true);
            return;
        }

        openEvidence(item.kind === 'project'
            ? { type: 'feedbax:widget:showcasestudy', key: item.project.key }
            : item.kind === 'socialpost'
                ? { type: 'feedbax:widget:showsocialpost', key: item.social.key }
                : { type: 'feedbax:widget:showreview', key: item.review.key });
    }

    /**
     * Opens the widget on one piece of evidence. Shared by the tapped card and by the host page's own
     * tiles (openEvidenceByKey), so both reach the drawer the same way.
     * @param {{type: string, key: string}} evidence - Message type and the validated key it carries
     */
    function openEvidence(evidence) {
        // Deliberately a message, never a frame URL parameter. A URL keeps its deep link for the life
        // of the frame, so every later reload of it — a dropped circuit reconnecting, for instance —
        // would reopen what the visitor had just closed, again and again. A message happens once.
        if (state.frameSubscribed) {
            postEvidenceToFrame(evidence);
        } else {
            state.pendingEvidence = evidence; // Replayed on the circuit's subscription handshake.
        }

        setOpen(true);
    }

    /**
     * LeadWin('review', key) and LeadWin('project', key): a review or project tile on the host page's
     * own website, opened inside the widget exactly as a tapped corner card opens it. The key is the one
     * the corner's cards carry (the review's or the case study's GUID) and is held to the same pattern
     * the config's cards are; the circuit re-reads it and shows nothing for a key of another provider.
     * A key that is no GUID leaves the panel closed, so a typo on the page does not open an empty chat.
     * @param {'review'|'project'} kind - Which drawer the key is for
     * @param {unknown} arg - The key, as a bare string
     * @returns {boolean} Whether the widget took it
     */
    function openEvidenceByKey(kind, arg) {
        const key = typeof arg === 'string' ? arg.trim() : '';
        if (!guidPattern.test(key)) {
            return false;
        }

        openEvidence({
            type: kind === 'project' ? 'feedbax:widget:showcasestudy' : 'feedbax:widget:showreview',
            key: key
        });

        return true;
    }

    /**
     * Hands one piece of evidence to the mounted circuit. Only a key travels; the page loads the review
     * or the project itself and refuses one belonging to another provider.
     * @param {{type: string, key: string}} evidence - Message type and the validated key it carries
     */
    function postEvidenceToFrame(evidence) {
        if (!state.iframe || !state.iframe.contentWindow) {
            return;
        }

        try {
            state.iframe.contentWindow.postMessage({ type: evidence.type, key: evidence.key }, origin);
        } catch { /* frame not loaded yet — the pending route covers it */ }
    }

    /**
     * Tells the mounted circuit which of the corner's groups this visitor has switched off. The choice
     * is made out here (localStorage belongs to the embedding page, and the corner lives on it), but the
     * way BACK belongs in the widget: the corner has no room for a settings screen, and a control that
     * only appears once something is hidden has nowhere else to appear. Sent on every change and once on
     * the circuit's handshake, so a widget opened later still knows what it may offer to undo.
     */
    function postTrustHiddenToFrame() {
        if (!state.iframe || !state.iframe.contentWindow || !state.frameSubscribed) {
            return;
        }

        const groups = trustOptOutGroups.filter(function (group) {
            return hiddenTrustGroups().has(group);
        });

        try {
            state.iframe.contentWindow.postMessage(
                { type: 'feedbax:widget:trusthidden', groups: groups }, origin);
        } catch { /* frame not loaded yet — the handshake replays this */ }
    }

    /**
     * The visitor took one group back, or all of them, from inside the widget. The corner is dealt
     * again right away rather than on the next page: they asked for it while looking at the page it
     * stands on, and telling them to reload would be a strange answer to "wieder anzeigen".
     * @param {unknown} group - One of the three groups, or 'all'
     */
    function applyTrustRestore(group) {
        const wanted = typeof group === 'string' ? group : '';
        if (wanted !== 'all' && trustOptOutGroups.indexOf(wanted) < 0) {
            return; // Unknown value — dropped like every other unknown message input.
        }

        if (!showTrustGroups(wanted)) {
            return; // Nothing was hidden under that name; nothing to rebuild.
        }

        postTrustHiddenToFrame();

        // The visit's "already shown" mark goes with it, for the reason a save broadcast clears it: a
        // visitor who just asked for the surface back would otherwise be told they had already had
        // their look at it.
        resetTrustSeen();
        applyLiveTrust(state.trustPayload);
        repaintForTheme(); // The fresh corner has to wear whatever look the page is in.
    }

    /**
     * Lifts the cards clear of whatever the corner nearest the launcher holds right now. Bubbles and
     * campaign cards keep that corner (they are the timed interruption; the trust cards are the standing
     * proof), so the board re-measures the stack instead of guessing at a fixed offset.
     */
    function positionTrustCards() {
        // The CORNER, not the board: after a dismissal the board is gone and the corner is still there
        // holding the opt-out offer, which has to keep clear of the bubbles exactly like the card did.
        if (!state.trustCardsCorner) {
            return;
        }

        // The bubbles or the unread card, whichever holds the slot (they never stand together).
        const stack = state.nudgeStack || state.unreadCard;
        const stackHeight = stack ? stack.offsetHeight : 0;
        // Riding the same safe area as the launcher and the bubbles it stands on (see createButton):
        // everything in this corner is measured from the launcher, so it all has to rise together above
        // the home indicator.
        state.trustCardsCorner.style.bottom =
            withSafeArea((stackHeight > 0 ? 78 + stackHeight + 12 : 78) + 'px', 'bottom');
    }

    /** Swaps in the next card and keeps going. Idempotent — a second start replaces the first timer. */
    function startTrustCardRotation() {
        // The corner is what the open panel hides, so it is what says whether anything is on screen.
        if (!state.trustCardsBoard || state.trustCardsCorner.style.display === 'none') {
            return;
        }

        stopTrustCardRotation();

        // One card needs no rotation, and a timer that only ever redraws the same one is waste on
        // somebody else's page.
        if (options.trustCards.items.length < 2) {
            return;
        }

        trustCardTimer = setInterval(function () {
            state.trustCardIndex = (state.trustCardIndex + 1) % options.trustCards.items.length;
            state.trustCardsBoard.show(state.trustCardIndex);
            positionTrustCards(); // A longer quote is a taller card; the stack below must not overlap.
        }, options.trustRotateMs);
    }

    function stopTrustCardRotation() {
        if (trustCardTimer !== null) {
            clearInterval(trustCardTimer);
            trustCardTimer = null;
        }
    }

    /** Hooks up a freshly built board and starts it. Called for the first one and after every save. */
    function wireTrustCards() {
        if (!state.trustCardsCorner) {
            return;
        }

        // Reading stops the clock: nobody should lose a card mid-sentence because a timer ran out. On
        // the CORNER rather than the board, because the close control hangs off the board's edge —
        // reaching for it left the board and restarted the very rotation the reach was meant to stop.
        state.trustCardsCorner.addEventListener('mouseenter', stopTrustCardRotation);
        state.trustCardsCorner.addEventListener('focusin', stopTrustCardRotation);
        state.trustCardsCorner.addEventListener('mouseleave', startTrustCardRotation);
        state.trustCardsCorner.addEventListener('focusout', startTrustCardRotation);
        startTrustCardRotation();
    }

    /**
     * Takes the launcher's trust surface down, and the timer with it. Used when a visit has had its
     * look; a save rebuilds the surface from scratch (applyLiveTrust) rather than bringing this one
     * back, because which FORMS stand there is part of what was saved.
     */
    function retireTrustSurface() {
        stopTrustCardRotation();
        clearTrustOptOutTimers();

        if (state.trustCardsCorner) {
            state.trustCardsCorner.remove(); // Takes the card, its close control and the offer with it.
            state.trustCardsCorner = null;
            state.trustCardsEl = null;
            state.trustCardsBoard = null;
        }
    }

    /** Cancels whatever the opt-out offer still has scheduled — itself, or its confirmation. */
    function clearTrustOptOutTimers() {
        while (trustOptOutTimers.length > 0) {
            clearTimeout(trustOptOutTimers.pop());
        }
    }

    /**
     * The close control's own path, and the mirror of what a dismissed bubble does: taking the corner
     * away is only half an answer, because a visitor who does not want this proof would otherwise have
     * to close it again on every page of the site. So the surface goes and the offer to stop being shown
     * this KIND of proof stands in its place — named after what was standing there, not after the corner
     * it stood in: somebody who does not care to read strangers' quotes may well want to see what the
     * provider built.
     * Falls straight back to the plain teardown where the offer would be pointless: no label from the
     * server, or a card whose form this loader does not know.
     */
    function dismissTrustCards() {
        const group = state.trustCardsBoard ? trustCardGroups[state.trustCardsBoard.shownKind()] : '';
        const label = group ? options.trustLabels.groups[group] : '';

        if (!state.trustCardsCorner || !label) {
            retireTrustSurface();
            return;
        }

        stopTrustCardRotation();
        clearTrustOptOutTimers();

        // Everything the visitor just dismissed goes at once, close control included: the wrapper stays
        // behind for the offer alone, so nothing they said no to is still standing there.
        while (state.trustCardsCorner.firstChild) {
            state.trustCardsCorner.firstChild.remove();
        }

        state.trustCardsEl = null;
        state.trustCardsBoard = null;

        state.trustCardsCorner.appendChild(createTrustOptOut(group, label));
        positionTrustCards();
        trustOptOutTimers.push(setTimeout(retireTrustSurface, optOutOfferMs));
    }

    /**
     * The opt-out chip, deliberately the bubbles' own: it is the same offer in the same place, and a
     * visitor who has met one should recognise the other. One tap stores the choice for this group on
     * this device, the chip says so, and the corner goes.
     * Nothing else is put back up in its stead — the corner is done for this page either way, and the
     * groups the visitor kept simply return on the next one. That is what "zukünftige Anzeige" means,
     * and re-dealing the rest on top of a dismissal would be arguing with them.
     * @param {string} group - The group this chip switches off
     * @param {string} label - Its localized wording ("Bewertungen ausblenden")
     * @returns {HTMLButtonElement}
     */
    function createTrustOptOut(group, label) {
        const optOut = document.createElement('button');
        optOut.type = 'button';
        optOut.className = 'lw-trust-optout';
        optOut.textContent = label;
        optOut.addEventListener('click', function () {
            hideTrustGroup(group);
            postTrustHiddenToFrame(); // The widget's way back only exists once it knows what is hidden.
            clearTrustOptOutTimers();

            // The confirmation replaces the offer in place. Without a localized one there is nothing
            // worth saying, and the corner simply leaves.
            if (options.trustLabels.done) {
                optOut.textContent = options.trustLabels.done;
                optOut.disabled = true;
                optOut.classList.add('lw-trust-optout-done');
            }

            trustOptOutTimers.push(setTimeout(retireTrustSurface, optOutDoneMs));
        });

        return optOut;
    }

    /**
     * "Nur beim ersten Besuch" inside a single-page application: no document load happens there, so the
     * route change is what ends the first page. The surface goes with it rather than standing on —
     * otherwise the two answers would be indistinguishable on exactly the sites that navigate without
     * reloading, which is most of them by now.
     */
    function hookTrustRouteChanges() {
        // The KEY the hook hands out below, not the bare pathname: seeded with a pathname, the first
        // route change on a site whose screens live in a query parameter always looks like a new page and
        // the corner would be dealt again on a page the visitor never left.
        let lastTrustPath = buildPageKey(runtime.pageQueryKeys);
        let lastTrustAllowed = urlRulesAllow(options.trustUrlRules);

        onPathChange(function (path) {
            // A contains rule may name a query parameter that is no page key (?tender=…), so the same
            // page can turn excluded and back. Such a flip is handled like a new page: the branches below
            // already know both directions.
            const allowed = urlRulesAllow(options.trustUrlRules);
            if (path === lastTrustPath && allowed === lastTrustAllowed) {
                return; // Same page: applications push state for filters and query strings too.
            }

            lastTrustPath = path;
            lastTrustAllowed = allowed;

            // Same guard the bubble hook carries: an instance whose launcher left the document has no
            // business touching a page it no longer belongs to.
            if (!button.isConnected) {
                return;
            }

            // A page the provider excluded takes the corner down whatever the rhythm says — the proof
            // was dealt on a page the visitor has left.
            if (!allowed) {
                retireTrustSurface();
                return;
            }

            if (options.trustEveryPage) {
                // A new page is a new look at a corner that simply stays standing: the card is not
                // re-dealt here (that would restart the rotation mid-read), so the impression of the
                // form on the board is reported from here instead. Without it the whole visit would
                // count as one look while the contractor asked for one per page.
                if (state.trustCardsBoard) {
                    analytics.onTrustImpression(state.trustCardsBoard.shownKind());
                    return;
                }

                // Nothing standing, and the visit is still owed a look: the visitor walked off an
                // excluded page onto an allowed one, so the corner they were kept from is dealt now.
                if (!trustAlreadySeen(true)) {
                    mountFreshBoard();
                    wireTrustCards();
                    positionTrustCards();
                }

                return;
            }

            retireTrustSurface();
        });
    }

    /**
     * Hangs a freshly built board off the shared state and marks the visit where something really
     * stands there. Both the first build and every rebuild end here, so the "already shown" mark can
     * never drift apart from what is actually on screen.
     * @param {object|null} board - The result of createTrustBoard, or null where nothing resolved
     */
    function mountBoard(board) {
        state.trustCardsBoard = board;
        state.trustCardsEl = board ? board.el : null;
        state.trustCardsCorner = board ? board.corner : null;

    // Marked once something really stands there, not when it was merely configured: a visit whose
    // corner resolved to nothing was never shown anything to remember.
    if (state.trustCardsEl) {
        markTrustSeen(options.trustEveryPage);
    }
    }

    /**
     * Builds a board for the page the visitor is on right now and hangs it up — or hangs up nothing,
     * where the corner has nothing to show or the provider excluded this page. The only place a board
     * is ever created, so the page rules cannot be honoured by one caller and forgotten by the next.
     */
    function mountFreshBoard() {
        mountBoard(options.trustCards && urlRulesAllow(options.trustUrlRules)
            ? createTrustBoard(options, openTrustCard, dismissTrustCards)
            : null);
    }

    /**
     * The corner as the manage panel just saved it. Used to live in applyLiveTrust, which had to
     * reach into the corner's own state to do it; the fingerprint bookkeeping stays there, the
     * rebuilding belongs here.
     * @param {object} data - The settings payload the frame broadcast
     */
    function rebuild(data) {
        // The payload the corner was built from, so a visitor taking a hidden group back can be dealt
        // the surface this widget would show them right now, not the one it showed on page load.
        state.trustPayload = data;

        stopTrustCardRotation();
        clearTrustOptOutTimers();
        state.trustCardIndex = 0;

        retireTrustSurface();

        options.trustCards = buildTrustDeck(data);
        options.trustLabels = resolveTrustLabels(data);

        const rhythm = resolveTrustRhythm(data);
        options.trustRotateMs = rhythm.trustRotateMs;
        options.trustEveryPage = rhythm.trustEveryPage;
        options.trustUrlRules = rhythm.trustUrlRules;

        // The visit's "already shown" mark goes, exactly like the bubbles' does on their broadcast:
        // otherwise a contractor who just saved the surface would have to hunt for a fresh tab to see
        // what they saved.
        resetTrustSeen();

        mountFreshBoard();

        wireTrustCards();
        positionTrustCards(); // The new card has its own height; the bubble stack must not overlap it.
    }

    /**
     * Wires the corner up. Separate from building it, and called at the point in init() where the
     * wiring used to sit: the listeners below outlive every rebuild, and moving them earlier would
     * change the order the instance comes up in.
     */
    function install() {
        // Registered once for the life of the instance, board or no board: the listeners above die with
        // the element they sit on, this one would pile up a copy per save.
        window.addEventListener('resize', positionTrustCards);
        wireTrustCards();
        hookTrustRouteChanges();
    }

    /* The corner's rotating proof, only where the server resolved some (switch on, a form ticked, data
     * verified). A save in the manage panel reaches it live, over the frame's own settings broadcast
     * (applyLiveTrust below); a visitor who never opened the widget gets the change on their next page
     * load, off the config.
     * ...and only where this visit is still owed a look at it. "Nur beim ersten Besuch" is the default:
     * proof works as a first impression, and a visitor meeting the same seal on their eighth page reads
     * it as advertising. The gate sits here rather than around the rendering, so a surface that stays
     * away costs the page no element at all. */
    state.trustCardIndex = 0;

    if (trustAlreadySeen(options.trustEveryPage)) {
        mountBoard(null);
    } else {
        mountFreshBoard();
    }

    return {
        install,
        rebuild,
        openTrustCard,
        openEvidenceByKey,
        positionTrustCards,
        startTrustCardRotation,
        stopTrustCardRotation,
        wireTrustCards,
        retireTrustSurface,
        clearTrustOptOutTimers,
        dismissTrustCards,
        applyTrustRestore,
        postEvidenceToFrame,
        postTrustHiddenToFrame
    };
}
